import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type NumberValue = string | number | null | undefined;

type CurrencyConverterContextType = {
  baseCurrency: string;
  targetCurrency: string;
  rate: number;
  locale: string;
  loading: boolean;
  error: string;
  convertFromMYR: (value: NumberValue) => number;
  formatConvertedCurrency: (value: NumberValue) => string;
  formatLocalCurrencyOnly: (
    value: NumberValue,
    options?: { showCode?: boolean },
  ) => string;
  formatMYRWithConverted: (value: NumberValue) => string;
  refreshCurrencyRate: () => Promise<void>;
};

const CurrencyConverterContext = createContext<
  CurrencyConverterContextType | undefined
>(undefined);

const API_BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";

const getStoredSubscriberCountry = () => {
  try {
    const raw = localStorage.getItem("subscriber_user");

    if (!raw) return "";

    const user = JSON.parse(raw);

    return user?.country || "";
  } catch {
    return "";
  }
};

const toSafeNumber = (value: NumberValue): number => {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const numericValue = Number(
    String(value)
      .replace(/RM/gi, "")
      .replace(/MYR/gi, "")
      .replace(/,/g, "")
      .trim(),
  );

  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const CurrencyConverterProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [baseCurrency, setBaseCurrency] = useState("MYR");
  const [targetCurrency, setTargetCurrency] = useState("MYR");
  const [rate, setRate] = useState(1);
  const [locale, setLocale] = useState("en-MY");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refreshCurrencyRate = async () => {
    const country = getStoredSubscriberCountry();

    if (!country) {
      setBaseCurrency("MYR");
      setTargetCurrency("MYR");
      setRate(1);
      setLocale("en-MY");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        `${API_BASE_URL}/currency/rate?country=${encodeURIComponent(country)}`,
        {
          credentials: "include",
        },
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to fetch currency rate.");
      }

      setBaseCurrency(result.data?.baseCurrency || "MYR");
      setTargetCurrency(result.data?.targetCurrency || "MYR");
      setRate(Number(result.data?.rate || 1));
      setLocale(result.data?.locale || "en-MY");
    } catch (err: any) {
      console.error("Currency conversion error:", err);

      setError(err.message || "Unable to fetch currency rate.");
      setBaseCurrency("MYR");
      setTargetCurrency("MYR");
      setRate(1);
      setLocale("en-MY");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshCurrencyRate();

    const handleFocus = () => {
      refreshCurrencyRate();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const convertFromMYR = (value: NumberValue) => {
    return toSafeNumber(value) * rate;
  };

  const formatMYR = (value: NumberValue) => {
    return new Intl.NumberFormat("en-MY", {
      style: "currency",
      currency: "MYR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(toSafeNumber(value));
  };

  const formatConvertedCurrency = (value: NumberValue) => {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: targetCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertFromMYR(value));
  };

  const formatLocalCurrencyOnly = (
    value: NumberValue,
    options?: { showCode?: boolean },
  ) => {
    const formattedAmount = new Intl.NumberFormat(locale, {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertFromMYR(value));

    if (options?.showCode) {
      return `${targetCurrency.toUpperCase()} ${formattedAmount}`;
    }

    return formattedAmount;
  };

  const formatMYRWithConverted = (value: NumberValue) => {
    const myrValue = formatMYR(value);

    if (targetCurrency === "MYR") {
      return myrValue;
    }

    return `${myrValue} ≈ ${formatConvertedCurrency(value)}`;
  };

  const contextValue = useMemo(
    () => ({
      baseCurrency,
      targetCurrency,
      rate,
      locale,
      loading,
      error,
      convertFromMYR,
      formatConvertedCurrency,
      formatLocalCurrencyOnly,
      formatMYRWithConverted,
      refreshCurrencyRate,
    }),
    [baseCurrency, targetCurrency, rate, locale, loading, error],
  );

  return (
    <CurrencyConverterContext.Provider value={contextValue}>
      {children}
    </CurrencyConverterContext.Provider>
  );
};

export const useCurrencyConverter = () => {
  const context = useContext(CurrencyConverterContext);

  if (!context) {
    throw new Error(
      "useCurrencyConverter must be used inside CurrencyConverterProvider",
    );
  }

  return context;
};
